/** Reads keyboard (WASD + arrows) and mouse (aim + fire) into tank controls. */
export class InputManager {
  private keys = new Set<string>();
  private firing = false;
  // Edge-triggered: the class special fires once per Shift press (not every
  // frame it's held). Set on keydown, drained by consumeAbility().
  private abilityQueued = false;
  // Relative mouse movement accumulated since the last consumeMouseDelta()
  // call (drained once per frame). Driving aim from movement DELTAS rather
  // than the cursor's absolute screen/world position means the target angle
  // only changes when the mouse actually moves — point somewhere, it turns
  // there, then genuinely stops, instead of continuously re-deriving a target
  // from where the cursor happens to sit. This ALSO requires Pointer Lock
  // (see main.ts) so the OS cursor never hits the window edge and stops
  // generating movement — without it, movementX silently drops to ~0 once
  // the (still-visible-position-wise) cursor is pinned at the boundary.
  private accumDx = 0;
  // Scroll wheel, accumulated the same way — drives the mortar aim distance
  // (see main.ts) now that there's no cursor position to derive it from.
  private accumWheel = 0;

  constructor() {
    window.addEventListener("keydown", this.onDown);
    window.addEventListener("keyup", this.onUp);
    window.addEventListener("blur", () => this.keys.clear());
    window.addEventListener("mousemove", this.onMove);
    window.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("wheel", this.onWheel, { passive: true });
    window.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  private onDown = (e: KeyboardEvent) => {
    if (e.code === "ShiftLeft" || e.code === "ShiftRight") {
      this.abilityQueued = true;
      e.preventDefault();
    }
    if (e.code.startsWith("Arrow")) e.preventDefault();
    this.keys.add(e.code);
  };
  private onUp = (e: KeyboardEvent) => this.keys.delete(e.code);
  private onMove = (e: MouseEvent) => {
    this.accumDx += e.movementX;
  };
  private onWheel = (e: WheelEvent) => {
    this.accumWheel += e.deltaY;
  };
  private onMouseDown = (e: MouseEvent) => {
    if (e.button === 0) this.firing = true;
  };
  private onMouseUp = (e: MouseEvent) => {
    if (e.button === 0) this.firing = false;
  };

  /** A/Left = turn left, D/Right = turn right (sign flipped for chase-cam convention). */
  getMove(): { fwd: number; turn: number } {
    let fwd = 0;
    let turn = 0;
    if (this.keys.has("KeyW") || this.keys.has("ArrowUp")) fwd += 1;
    if (this.keys.has("KeyS") || this.keys.has("ArrowDown")) fwd -= 1;
    if (this.keys.has("KeyA") || this.keys.has("ArrowLeft")) turn += 1;
    if (this.keys.has("KeyD") || this.keys.has("ArrowRight")) turn -= 1;
    return { fwd, turn };
  }

  /** Drains and returns the horizontal mouse movement (px) accumulated since the last call. */
  consumeMouseDeltaX(): number {
    const dx = this.accumDx;
    this.accumDx = 0;
    return dx;
  }

  /** Drains and returns the scroll wheel's deltaY accumulated since the last call. */
  consumeWheelDeltaY(): number {
    const w = this.accumWheel;
    this.accumWheel = 0;
    return w;
  }

  isFiring(): boolean {
    return this.firing;
  }

  /** True once per Shift press (drains the edge). Drives the class special. */
  consumeAbility(): boolean {
    if (this.abilityQueued) {
      this.abilityQueued = false;
      return true;
    }
    return false;
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onDown);
    window.removeEventListener("keyup", this.onUp);
    window.removeEventListener("mousemove", this.onMove);
    window.removeEventListener("mousedown", this.onMouseDown);
    window.removeEventListener("mouseup", this.onMouseUp);
    window.removeEventListener("wheel", this.onWheel);
  }
}
